import React from 'react';
import { IconType } from 'react-icons';
import {
  FiBell, FiCalendar, FiFileText, FiFlag, FiMap, FiMapPin, FiMusic, FiRotateCcw, FiRss, FiSliders, FiTag, FiTruck,
  FiUsers, FiVolume2,
} from 'react-icons/fi';
import { EditorType } from '../../../../model/Roles';
import { useMessages } from '../../../../i18n/I18n';
import './EditorMenu.css';

interface EditorMenuProps {
  version: number;
  allowedEditors: EditorType[];
  activeEditor: EditorType;
  onTabChange: (newTab: EditorType) => void;
  /** The pointer or focus reached a tab: it's likely to open next */
  onTabIntent?: (tab: EditorType) => void;
}

// Their labels are in the editor messages (tabs)
const TAB_ICONS: Record<EditorType, IconType> = {
  infos: FiSliders,
  map: FiMap,
  tickets: FiTag,
  pois: FiMapPin,
  artists: FiMusic,
  shows: FiCalendar,
  history: FiRotateCcw,
  pages: FiFileText,
  news: FiRss,
  notifications: FiBell,
  users: FiUsers,
  carpools: FiTruck,
  reporting: FiFlag,
  advertisements: FiVolume2,
};

const festivalEditors: EditorType[] = ['infos', 'map', 'tickets', 'pois', 'artists', 'shows', 'history'];
const contentEditors: EditorType[] = ['pages', 'news', 'notifications', 'users', 'carpools', 'reporting', 'advertisements'];

const EditorMenu: React.FC<EditorMenuProps> = ({ version, allowedEditors, activeEditor, onTabChange, onTabIntent }) => {
  const t = useMessages('editor');
  const renderSection = (title: string, editors: EditorType[]) => {
    const visibleEditors = editors.filter(editor => allowedEditors.includes(editor));
    if (visibleEditors.length === 0) return null;

    return (
      <div className="editor-section">
        <h2 className="section-title">{title}</h2>
        <ul>
          {visibleEditors.map(editor => {
            const label = t.tabs[editor];
            const Icon = TAB_ICONS[editor];
            const isActive = activeEditor === editor;
            return (
              <li key={editor} className={isActive ? 'active' : ''}>
                <button
                  type="button"
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => onTabChange(editor)}
                  onMouseEnter={() => !isActive && onTabIntent?.(editor)}
                  onFocus={() => !isActive && onTabIntent?.(editor)}
                >
                  <Icon aria-hidden="true" />
                  <span>{label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <nav className="editor-menu" aria-label={t.menuLabel}>
      {renderSection(t.savedWithVersion(version), festivalEditors)}
      {renderSection(t.liveSection, contentEditors)}
    </nav>
  );
};

export default EditorMenu;
